import { Box, Tooltip, Typography } from "@mui/material";
import { useEffect } from "react";
import HelpIcon from '@mui/icons-material/Help';
import { calculateExpirationDate, MODIFICATION, RENEWAL } from "./helpers";

export default function TotalModalPay({
    products,
    planType,
    selectedPlan,
    credit,
    setCalculatedTotal,
    currency,
    setTotalInARS,
    usdCotization,
    fixedCosts,
    purchaseType,
    lastPurchase,
    previousAmountPaid
}) {
    const MONTHLY = 1;
    const ANNUAL = 2;

    const multiplierWithDiscount = planType === MONTHLY ? 1 : 0.15;
    const multiplierDynamic = planType === MONTHLY ? 1 : 12;

    const totalPerks = products.reduce(
        (acc, product) => acc + product.newPrice * product.quantity,
        0
    ) - (purchaseType === MODIFICATION ? previousAmountPaid : 0) * multiplierDynamic;

    let basePlanPrice;

    if (purchaseType === MODIFICATION) {
        const isDifferentPlan =
            planType !== getIdPurchaseType(lastPurchase.duracion);

        basePlanPrice = isDifferentPlan
            ? selectedPlan?.newPrice
            : 0;
    } else {
        basePlanPrice = selectedPlan?.price;
    }

    const planPriceWithoutCredit = basePlanPrice * multiplierDynamic;
    const fixedCostTotal = (purchaseType === MODIFICATION && (planType === getIdPurchaseType(lastPurchase.duracion)))
        ? 0
        : fixedCosts?.reduce((acc, item) => acc + item.price * (multiplierDynamic), 0) || 0;
    const subtotal = (planPriceWithoutCredit + totalPerks + fixedCostTotal)
    const annualDiscount = subtotal * multiplierWithDiscount
    const subtotalWithDiscount = subtotal - (planType === MONTHLY ? 0 : annualDiscount)
    const totalWithCredit = subtotalWithDiscount - credit + ((lastPurchase?.costo_deuda && purchaseType === RENEWAL) ? returnAmountAccordingToCurrency(lastPurchase?.costo_deuda) : 0);

    const creditApplied = (credit > (subtotalWithDiscount)) ? (subtotalWithDiscount) : credit

    const totalFinal = totalWithCredit * multiplierWithDiscount;



    useEffect(() => {
        const final = Math.max(totalFinal, 0);
        setCalculatedTotal(final);
        setTotalInARS(currency === "USD" ? totalWithCredit * usdCotization : totalWithCredit);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [products, planType, selectedPlan, credit, multiplierDynamic]);

    const formatPrice = (price) => currency === "ARS"
        ? `$ ${price} ARS`
        : `U$S ${price}`;

    const formatToLocaleString = (number) =>
        Number(number).toLocaleString('es-AR', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        });

    function returnAmountAccordingToCurrency(amount) {
        if (currency === 'ARS') {
            return amount * usdCotization
        } else {
            return (amount);
        }
    }

    function getIdPurchaseType(purchaseType) {
        if (purchaseType === "Mensual") return 1;
        if (purchaseType === "Anual") return 2;
    }

    return (
        <>
            {((purchaseType !== MODIFICATION) || (purchaseType === MODIFICATION && (planType !== getIdPurchaseType(lastPurchase.duracion)))) && (
                <Box display="flex" justifyContent="space-between" mt={1}>
                    <Typography variant="body1">Precio del plan</Typography>
                    <Typography variant="body1" fontWeight="bold">
                        {formatPrice(formatToLocaleString(planPriceWithoutCredit))}
                    </Typography>
                </Box>
            )}

            <Box display="flex" justifyContent="space-between">
                <Typography variant="body1">Total de extras</Typography>
                <Typography variant="body1" fontWeight="bold">
                    {formatPrice(formatToLocaleString(totalPerks))}
                </Typography>
            </Box>

            {(purchaseType !== MODIFICATION && fixedCosts?.length > 0) &&
                fixedCosts.map((item, i) => (
                    <Box key={i} display="flex" justifyContent="space-between">
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body1">{item.descripcion}</Typography>
                            <Tooltip title={item.price < 0 ? "Bonificación aplicada a tu cuenta" : "Gastos fijos asignados a tu cuenta"} placement="top">
                                <HelpIcon fontSize="small" color={item.price < 0 ? "success" : "primary"} />
                            </Tooltip>
                        </Box>
                        <Typography
                            variant="body1"
                            fontWeight="bold"
                            style={item.price < 0 ? { color: 'green' } : {}}>
                            {formatPrice(formatToLocaleString(item.price * (planType === MONTHLY ? 1 : 12)))}
                        </Typography>
                    </Box>
                ))
            }

            <Box display="flex" justifyContent="space-between">
                <Typography variant="body1" fontWeight={"bold"}>Subtotal:</Typography>
                <Typography variant="body1" fontWeight="bold">
                    {formatPrice(formatToLocaleString(subtotal))}
                </Typography>
            </Box>

            {
                (lastPurchase?.costo_deuda > 0 && purchaseType === RENEWAL) && (
                    <Box style={{ color: 'red' }} display="flex" justifyContent="space-between">
                        <Typography variant="body1">Período adeudado:</Typography>
                        <Typography variant="body1" fontWeight="bold">
                            {currency === 'ARS'
                                ? ` $${formatToLocaleString(Math.max(lastPurchase.costo_deuda, 0) * usdCotization)} ARS`
                                : ` ${formatPrice(formatToLocaleString(Math.max(lastPurchase.costo_deuda, 0)))}`
                            }
                        </Typography>
                    </Box>
                )
            }

            {planType !== MONTHLY && (
                <Box display="flex" justifyContent="space-between">
                    <Typography variant="body1">Descuento anual:</Typography>
                    <Typography variant="body1" fontWeight="bold" color="error">
                        -{formatPrice(formatToLocaleString(annualDiscount))}
                    </Typography>
                </Box>
            )}

            {credit > 0 && (
                <Box display="flex" justifyContent="space-between">
                    <Typography variant="body1">Crédito aplicado:</Typography>
                    <Typography variant="body1" fontWeight="bold" style={{ color: 'green' }}>
                        -{formatPrice(formatToLocaleString(creditApplied))}
                    </Typography>
                </Box>
            )}

            {currency === "USD" && (
                <Box display="flex" justifyContent="space-between">
                    <Typography variant="body1">Cotización USD:</Typography>
                    <Typography variant="body1" fontWeight="bold">
                        ${formatToLocaleString(usdCotization)} ARS
                    </Typography>
                </Box>
            )}

            <Box display="flex" justifyContent="space-between" mt={2}>
                <Typography variant="h6" fontWeight="bold">Total:</Typography>
                <Typography variant="h6" fontWeight="bold" color="primary">
                    {currency === 'USD'
                        ? `$${formatToLocaleString(Math.max(totalWithCredit, 0) * usdCotization)} ARS`
                        : formatPrice(formatToLocaleString(Math.max(totalWithCredit, 0)))}
                </Typography>
            </Box>

            {((purchaseType !== MODIFICATION) || (purchaseType === MODIFICATION && (planType !== getIdPurchaseType(lastPurchase.duracion)))) && (
                <Typography variant="body1" color="primary" mt={1} fontWeight="bold">
                    Plan {selectedPlan?.name} - {planType === MONTHLY ? 'Mensual' : 'Anual'}
                </Typography>
            )}

            {purchaseType === RENEWAL && lastPurchase?.dias_adeudados <= 0 && (
                <Typography variant="body1" mt={1} fontWeight="bold">
                    Próximo vencimiento: {calculateExpirationDate(
                        planType === ANNUAL
                            ? lastPurchase?.prox_vencimiento_anual
                            : lastPurchase?.prox_vencimiento_mensual
                    )}
                </Typography>
            )}

        </>
    );
}
